import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  serverTimestamp,
  limit,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';

// Firestore timestamp type - can be Timestamp, Date, or null
// Date is allowed as input since Firestore converts it to Timestamp
type FirestoreTimestamp = Timestamp | Date | null;

// Trial duration in days
export const TRIAL_DURATION_DAYS = 3;

// Pagination limits to prevent memory issues at scale
const MAX_IMAGES_PER_USER = 50;
const MAX_RECIPES_PER_USER = 100;
const MAX_USAGE_RECORDS = 50;

// Payment method type
export type PaymentMethod = 'none' | 'trial' | 'stripe';

// User Profile interface
export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  has_paid: boolean;
  payment_method: PaymentMethod;
  trial_start_date: FirestoreTimestamp;
  food_genres: string[] | null;
  dietary_preferences: string | null;
  subscription_tier: string;
  created_at: FirestoreTimestamp;
  updated_at: FirestoreTimestamp;
}

// Helper to convert FirestoreTimestamp to Date
function toDate(timestamp: FirestoreTimestamp): Date | null {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  // Firestore Timestamp has toDate() method
  if ('toDate' in timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  return null;
}

// Helper function to check if trial is expired
export function isTrialExpired(profile: UserProfile): boolean {
  if (!profile.trial_start_date) return false;

  const trialStart = toDate(profile.trial_start_date);
  if (!trialStart) return false;

  const now = new Date();
  const diffTime = now.getTime() - trialStart.getTime();
  const diffDays = diffTime / (1000 * 60 * 60 * 24);

  return diffDays > TRIAL_DURATION_DAYS;
}

// Helper to get days remaining in trial
export function getTrialDaysRemaining(profile: UserProfile): number {
  if (!profile.trial_start_date) return 0;

  const trialStart = toDate(profile.trial_start_date);
  if (!trialStart) return 0;

  const now = new Date();
  const diffTime = now.getTime() - trialStart.getTime();
  const diffDays = diffTime / (1000 * 60 * 60 * 24);

  return Math.max(0, Math.ceil(TRIAL_DURATION_DAYS - diffDays));
}

// User Image interface
export interface UserImage {
  id: string;
  user_id: string;
  image_url: string;
  image_name: string;
  created_at: FirestoreTimestamp;
}

// Saved Recipe interface
export interface SavedRecipe {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  ingredients: string[];
  instructions: string[];
  prep_time: number;
  cook_time: number;
  servings: number;
  cuisine: string[];
  dietary_tags: string[];
  difficulty: string;
  tips: string[] | null;
  variations: string[] | null;
  is_generated: boolean;
  created_at: FirestoreTimestamp;
  updated_at: FirestoreTimestamp;
}

// Usage Tracking interface
export interface UsageRecord {
  id: string;
  user_id: string;
  action_type: 'recipe_generation' | 'ingredient_analysis';
  recipes_generated: number;
  created_at: FirestoreTimestamp;
}

// User Ingredients interface
export interface UserIngredients {
  id: string;
  user_id: string;
  ingredients: string[];
  last_updated: FirestoreTimestamp;
}

// Meal Plan interfaces
export interface MealSlot {
  recipe_id: string;
  recipe_title: string;
  servings: number;
}

export interface DayPlan {
  breakfast: MealSlot | null;
  lunch: MealSlot | null;
  dinner: MealSlot | null;
}

export interface MealPlan {
  id: string;
  user_id: string;
  week_start: FirestoreTimestamp;
  days: {
    monday: DayPlan;
    tuesday: DayPlan;
    wednesday: DayPlan;
    thursday: DayPlan;
    friday: DayPlan;
    saturday: DayPlan;
    sunday: DayPlan;
  };
  created_at: FirestoreTimestamp;
  updated_at: FirestoreTimestamp;
}

// Recipe Rating interface
export interface RecipeRating {
  id: string;
  user_id: string;
  recipe_id: string;
  recipe_title: string;
  rating: 1 | 2 | 3 | 4 | 5;
  notes: string | null;
  would_make_again: boolean;
  created_at: FirestoreTimestamp;
  updated_at: FirestoreTimestamp;
}

export class FirestoreService {
  // Profile methods
  static async getProfile(userId: string): Promise<UserProfile | null> {
    try {
      const docRef = doc(db, 'profiles', userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as UserProfile;
      }
      return null;
    } catch (error) {
      console.error('Error getting profile:', error);
      throw error;
    }
  }

  static async createProfile(userId: string, profileData: Omit<UserProfile, 'id' | 'created_at' | 'updated_at'>): Promise<UserProfile> {
    try {
      const docRef = doc(db, 'profiles', userId);
      const newProfile = {
        ...profileData,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      };

      await setDoc(docRef, newProfile);

      return { id: userId, ...newProfile } as UserProfile;
    } catch (error) {
      console.error('Error creating profile:', error);
      throw error;
    }
  }

  static async updateProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile> {
    try {
      const docRef = doc(db, 'profiles', userId);
      const updateData = {
        ...updates,
        updated_at: serverTimestamp()
      };

      await updateDoc(docRef, updateData);

      // Return updated profile
      const updated = await this.getProfile(userId);
      if (!updated) throw new Error('Profile not found after update');
      return updated;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  // User Images methods
  static async getUserImages(userId: string, maxResults: number = MAX_IMAGES_PER_USER): Promise<UserImage[]> {
    try {
      const q = query(
        collection(db, 'user_images'),
        where('user_id', '==', userId),
        orderBy('created_at', 'desc'),
        limit(maxResults)
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserImage[];
    } catch (error) {
      console.error('Error getting user images:', error);
      throw error;
    }
  }

  static async addUserImage(imageData: Omit<UserImage, 'id' | 'created_at'>): Promise<UserImage> {
    try {
      const docRef = await addDoc(collection(db, 'user_images'), {
        ...imageData,
        created_at: serverTimestamp()
      });

      return { id: docRef.id, ...imageData, created_at: new Date() } as UserImage;
    } catch (error) {
      console.error('Error adding user image:', error);
      throw error;
    }
  }

  static async deleteUserImage(imageId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'user_images', imageId));
    } catch (error) {
      console.error('Error deleting user image:', error);
      throw error;
    }
  }

  static async deleteUserImageByUrl(imageUrl: string, userId: string): Promise<void> {
    try {
      const q = query(
        collection(db, 'user_images'),
        where('image_url', '==', imageUrl),
        where('user_id', '==', userId)
      );
      const querySnapshot = await getDocs(q);

      // Use Promise.all with map to properly await all deletions
      await Promise.all(
        querySnapshot.docs.map((docSnapshot) => deleteDoc(docSnapshot.ref))
      );
    } catch (error) {
      console.error('Error deleting user image by URL:', error);
      throw error;
    }
  }

  // Saved Recipes methods
  static async getUserRecipes(userId: string, maxResults: number = MAX_RECIPES_PER_USER): Promise<SavedRecipe[]> {
    try {
      const q = query(
        collection(db, 'saved_recipes'),
        where('user_id', '==', userId),
        orderBy('created_at', 'desc'),
        limit(maxResults)
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SavedRecipe[];
    } catch (error) {
      console.error('Error getting user recipes:', error);
      throw error;
    }
  }

  static async saveRecipe(recipeData: Omit<SavedRecipe, 'id' | 'created_at' | 'updated_at'>): Promise<SavedRecipe> {
    try {
      const docRef = await addDoc(collection(db, 'saved_recipes'), {
        ...recipeData,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      });

      return { id: docRef.id, ...recipeData } as SavedRecipe;
    } catch (error) {
      console.error('Error saving recipe:', error);
      throw error;
    }
  }

  static async deleteRecipe(recipeId: string, userId?: string): Promise<void> {
    try {
      // Verify ownership before deleting
      if (userId) {
        const recipeDoc = await getDoc(doc(db, 'saved_recipes', recipeId));
        if (!recipeDoc.exists()) {
          throw new Error('Recipe not found');
        }
        const recipeData = recipeDoc.data();
        if (recipeData.user_id !== userId) {
          throw new Error('Not authorized to delete this recipe');
        }
      }
      await deleteDoc(doc(db, 'saved_recipes', recipeId));
    } catch (error) {
      console.error('Error deleting recipe:', error);
      throw error;
    }
  }

  // User Ingredients methods
  static async getUserIngredients(userId: string): Promise<string[]> {
    try {
      const docRef = doc(db, 'user_ingredients', userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as UserIngredients;
        return data.ingredients || [];
      }
      return [];
    } catch (error) {
      console.error('Error getting user ingredients:', error);
      throw error;
    }
  }

  static async saveUserIngredients(userId: string, ingredients: string[]): Promise<void> {
    try {
      const docRef = doc(db, 'user_ingredients', userId);
      await setDoc(docRef, {
        user_id: userId,
        ingredients: ingredients,
        last_updated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error saving user ingredients:', error);
      throw error;
    }
  }

  static async clearUserIngredients(userId: string): Promise<void> {
    try {
      const docRef = doc(db, 'user_ingredients', userId);
      await setDoc(docRef, {
        user_id: userId,
        ingredients: [],
        last_updated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error clearing user ingredients:', error);
      throw error;
    }
  }

  // Usage Tracking methods
  static async trackUsage(usageData: Omit<UsageRecord, 'id' | 'created_at'>): Promise<UsageRecord> {
    try {
      const docRef = await addDoc(collection(db, 'usage_tracking'), {
        ...usageData,
        created_at: serverTimestamp()
      });

      return { id: docRef.id, ...usageData, created_at: new Date() } as UsageRecord;
    } catch (error) {
      console.error('Error tracking usage:', error);
      throw error;
    }
  }

  static async getUserUsageRecords(userId: string, actionType: string, daysBack: number = 7): Promise<UsageRecord[]> {
    try {
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - daysBack);

      const q = query(
        collection(db, 'usage_tracking'),
        where('user_id', '==', userId),
        where('action_type', '==', actionType),
        where('created_at', '>=', daysAgo),
        orderBy('created_at', 'desc'),
        limit(MAX_USAGE_RECORDS)
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UsageRecord[];
    } catch (error) {
      console.error('Error getting user usage records:', error);
      throw error;
    }
  }

  // Meal Plan methods
  static async getMealPlan(userId: string, weekStart: Date): Promise<MealPlan | null> {
    try {
      const startOfWeek = new Date(weekStart);
      startOfWeek.setHours(0, 0, 0, 0);

      const q = query(
        collection(db, 'meal_plans'),
        where('user_id', '==', userId),
        where('week_start', '==', startOfWeek),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() } as MealPlan;
      }
      return null;
    } catch (error) {
      console.error('Error getting meal plan:', error);
      throw error;
    }
  }

  static async saveMealPlan(mealPlan: Omit<MealPlan, 'id' | 'created_at' | 'updated_at'>): Promise<MealPlan> {
    try {
      // Check if plan exists for this week
      const existing = await this.getMealPlan(
        mealPlan.user_id,
        mealPlan.week_start instanceof Date ? mealPlan.week_start : new Date()
      );

      if (existing) {
        // Update existing plan
        const docRef = doc(db, 'meal_plans', existing.id);
        await updateDoc(docRef, {
          days: mealPlan.days,
          updated_at: serverTimestamp()
        });
        return { ...existing, days: mealPlan.days, updated_at: new Date() };
      }

      // Create new plan
      const mealPlanData = {
        ...mealPlan,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'meal_plans'), mealPlanData);
      return { id: docRef.id, ...mealPlanData, created_at: new Date(), updated_at: new Date() } as MealPlan;
    } catch (error) {
      console.error('Error saving meal plan:', error);
      throw error;
    }
  }

  static async getUserMealPlans(userId: string, maxResults: number = 10): Promise<MealPlan[]> {
    try {
      const q = query(
        collection(db, 'meal_plans'),
        where('user_id', '==', userId),
        orderBy('week_start', 'desc'),
        limit(maxResults)
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as MealPlan[];
    } catch (error) {
      console.error('Error getting user meal plans:', error);
      throw error;
    }
  }

  // Recipe Rating methods
  static async rateRecipe(rating: Omit<RecipeRating, 'id' | 'created_at' | 'updated_at'>): Promise<RecipeRating> {
    try {
      // Check if user already rated this recipe
      let existing: RecipeRating | null = null;
      try {
        existing = await this.getUserRating(rating.user_id, rating.recipe_id);
      } catch (lookupError) {
        // Index may not exist yet - proceed to create new rating
        console.warn('Could not check existing rating (index may be missing):', lookupError);
      }

      if (existing) {
        // Update existing rating
        const docRef = doc(db, 'recipe_ratings', existing.id);
        await updateDoc(docRef, {
          rating: rating.rating,
          notes: rating.notes,
          would_make_again: rating.would_make_again,
          updated_at: serverTimestamp()
        });
        return { ...existing, ...rating, updated_at: new Date() };
      }

      // Create new rating
      const ratingData = {
        ...rating,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'recipe_ratings'), ratingData);
      return { id: docRef.id, ...ratingData, created_at: new Date(), updated_at: new Date() } as RecipeRating;
    } catch (error) {
      console.error('Error rating recipe:', error);
      throw error;
    }
  }

  static async getUserRating(userId: string, recipeId: string): Promise<RecipeRating | null> {
    try {
      const q = query(
        collection(db, 'recipe_ratings'),
        where('user_id', '==', userId),
        where('recipe_id', '==', recipeId),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() } as RecipeRating;
      }
      return null;
    } catch (error) {
      console.error('Error getting user rating:', error);
      return null;
    }
  }

  static async getRecipeRatings(recipeId: string): Promise<RecipeRating[]> {
    try {
      const q = query(
        collection(db, 'recipe_ratings'),
        where('recipe_id', '==', recipeId),
        orderBy('created_at', 'desc'),
        limit(50)
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as RecipeRating[];
    } catch (error) {
      console.error('Error getting recipe ratings:', error);
      throw error;
    }
  }

  static async getUserRatings(userId: string): Promise<RecipeRating[]> {
    try {
      const q = query(
        collection(db, 'recipe_ratings'),
        where('user_id', '==', userId),
        orderBy('created_at', 'desc'),
        limit(50)
      );
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as RecipeRating[];
    } catch (error) {
      console.error('Error getting user ratings:', error);
      return [];
    }
  }
}